import React, { useEffect, useState } from 'react';
import { GetTCards_GetItems } from '../../../Core/Service/T-card-services';
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Checkbox,
    CircularProgress,
    Box,
    Typography
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/styles';
import { SecondaryButton } from '../../../Core/FormInput';
import formatters from '../../../Core/Grid/inputFormatter';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        flex: 1,
        minHeight: 0,
        width: '100%'
    },
    tableContainer: {
        flex: 1,
        minHeight: 0,
        height: '100%',
        overflowY: 'auto',
        border: '1px solid rgba(0, 0, 0, 0.15)',
        borderRadius: 4,
        backgroundColor: '#fff',
        '&::-webkit-scrollbar': {
            width: 8,
            height: 8
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 4
        }
    },
    table: {
        minWidth: 900,
        borderCollapse: 'separate'
    },
    tableRow: {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 152, 0, 0.12) !important'
        }
    },
    selectedRow: {
        backgroundColor: 'rgba(255, 152, 0, 0.22) !important'
    },
    centerBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        height: '100%',
        minHeight: 200,
        gap: 12
    },
    submitButtonContainer: {
        marginTop: 12,
        flexShrink: 0
    }
}));

const StyledTableCell = withStyles(() => ({
    head: {
        fontSize: 14,
        fontWeight: 700,
        backgroundColor: '#e8e8e8',
        color: '#2d3748',
        padding: '10px 12px',
        borderBottom: '2px solid #cbd5e0'
    },
    body: {
        fontSize: 13,
        padding: '8px 12px',
        borderBottom: '1px solid #edf2f7'
    }
}))(TableCell);

const ImportTCardTable = ({ tCardsID, onSubmitImportTCardListHandler }) => {
    const classes = useStyles();
    const [tCardLineItems, setTCardLineItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAllSelect, setIsAllSelect] = useState(false);

    useEffect(() => {
        const fetchTCardItems = async () => {
            setLoading(true);
            try {
                const res = await GetTCards_GetItems(tCardsID);
                setTCardLineItems(res?.data?.tCards || []);
            } catch (err) {
                console.error('Error loading TCard items:', err);
            } finally {
                setLoading(false);
            }
        };
        if (tCardsID) {
            fetchTCardItems();
        } else {
            setLoading(false);
        }
    }, [tCardsID]);

    const onJobSelect = (checked, tCardsLineItemsID) => {
        setTCardLineItems((prevItems) => {
            const updated = prevItems.map((item) =>
                item.tCardsLineItemsID === tCardsLineItemsID ? { ...item, isSelected: checked } : item
            );
            setIsAllSelect(updated.length > 0 && updated.every((item) => item.isSelected));
            return updated;
        });
    };

    const onJobAllSelect = (e) => {
        const checked = e.target.checked;
        setIsAllSelect(checked);
        setTCardLineItems((prevItems) => prevItems.map((item) => ({ ...item, isSelected: checked })));
    };

    const toggleRow = (tCardsLineItemsID) => {
        const currentItem = tCardLineItems.find((q) => q.tCardsLineItemsID === tCardsLineItemsID);
        if (currentItem) {
            onJobSelect(!currentItem.isSelected, tCardsLineItemsID);
        }
    };

    const handleSubmit = () => {
        onSubmitImportTCardListHandler(tCardLineItems.filter((q) => q.isSelected));
    };

    const selectedCount = tCardLineItems.filter((q) => q.isSelected).length;

    return (
        <Box className={classes.root}>
            {loading ? (
                <Box className={classes.centerBox}>
                    <CircularProgress size={36} color="secondary" />
                    <Typography variant="body2" color="textSecondary">
                        Loading T-Card items...
                    </Typography>
                </Box>
            ) : tCardLineItems.length === 0 ? (
                <Box className={classes.centerBox}>
                    <Typography variant="body1" color="textSecondary">
                        No T-Card items available
                    </Typography>
                </Box>
            ) : (
                <TableContainer className={classes.tableContainer}>
                    <Table className={classes.table} size="small" stickyHeader aria-label="T-Card Items Table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell width="5%">
                                    <Checkbox
                                        name="isAllSelect"
                                        checked={isAllSelect || false}
                                        color="primary"
                                        onChange={onJobAllSelect}
                                    />
                                </StyledTableCell>
                                <StyledTableCell width="25%">Description/Comments</StyledTableCell>
                                <StyledTableCell width="25%">Cause</StyledTableCell>
                                <StyledTableCell width="25%">Cure</StyledTableCell>
                                <StyledTableCell width="10%">Event Type</StyledTableCell>
                                <StyledTableCell width="10%">Due Date</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tCardLineItems.map((q) => {
                                const isSelected = Boolean(q.isSelected);
                                return (
                                    <TableRow
                                        key={q.tCardsLineItemsID}
                                        hover
                                        role="checkbox"
                                        className={`${classes.tableRow} ${isSelected ? classes.selectedRow : ''}`}
                                        onClick={() => toggleRow(q.tCardsLineItemsID)}
                                    >
                                        <StyledTableCell onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={isSelected}
                                                color="primary"
                                                onChange={(e) => onJobSelect(e.target.checked, q.tCardsLineItemsID)}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell>{q.tCardsLineItemsDescription || '-'}</StyledTableCell>
                                        <StyledTableCell>{q.cause || '-'}</StyledTableCell>
                                        <StyledTableCell>{q.cure || '-'}</StyledTableCell>
                                        <StyledTableCell>{q.event || '-'}</StyledTableCell>
                                        <StyledTableCell>{formatters.YearFormatter(q.tCardsLineItemsDueDate)}</StyledTableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Box className={classes.submitButtonContainer}>
                <SecondaryButton
                    fullWidth
                    onClick={handleSubmit}
                    disabled={selectedCount <= 0 || loading}
                >
                    Submit {selectedCount > 0 ? `(${selectedCount})` : ''}
                </SecondaryButton>
            </Box>
        </Box>
    );
};

export default ImportTCardTable;
